import { useState, useEffect, useCallback } from 'react';
import { seedAPI, founderDashboardAPI } from '../services/api';
import JsonViewer from '../components/JsonViewer';
import {
  UserGroupIcon,
  PlusIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  SparklesIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  MinusIcon
} from '@heroicons/react/24/outline';

// Persona definitions with expected health scores and descriptions
const PERSONA_DEFINITIONS = {
  thriving: {
    name: 'Thriving',
    score: '85-95%',
    trend: 'stable',
    color: 'green',
    icon: SparklesIcon,
    description: '9 healthy, 1 needs attention. High engagement, strong attendance.',
    memberMix: { healthy: 9, needsAttention: 1, atRisk: 0 }
  },
  growing: {
    name: 'Growing',
    score: '75-85%',
    trend: 'up',
    color: 'blue',
    icon: ArrowTrendingUpIcon,
    description: '7 healthy, 2 needs attention, 1 at-risk (improving). 3 new members.',
    memberMix: { healthy: 7, needsAttention: 2, atRisk: 1 }
  },
  stable: {
    name: 'Stable',
    score: '65-75%',
    trend: 'flat',
    color: 'yellow',
    icon: MinusIcon,
    description: '6 healthy, 3 needs attention, 1 at-risk. Moderate engagement.',
    memberMix: { healthy: 6, needsAttention: 3, atRisk: 1 }
  },
  struggling: {
    name: 'Struggling',
    score: '45-60%',
    trend: 'down',
    color: 'orange',
    icon: ArrowTrendingDownIcon,
    description: '4 healthy, 2 needs attention, 4 at-risk (some consecutive absences).',
    memberMix: { healthy: 4, needsAttention: 2, atRisk: 4 }
  },
  crisis: {
    name: 'Crisis',
    score: '20-40%',
    trend: 'down',
    color: 'red',
    icon: ExclamationTriangleIcon,
    description: '2 healthy, 2 needs attention, 6 at-risk (long absence streaks).',
    memberMix: { healthy: 2, needsAttention: 2, atRisk: 6 }
  },
  recovering: {
    name: 'Recovering',
    score: '40-55%',
    trend: 'up',
    color: 'purple',
    icon: ArrowTrendingUpIcon,
    description: '2 healthy, 3 needs attention (improved), 5 at-risk (trending up).',
    memberMix: { healthy: 2, needsAttention: 3, atRisk: 5 }
  }
};

export default function PersonaSeedPage() {
  // Organization selection
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState('');
  const [ministries, setMinistries] = useState([]);
  const [selectedMinistry, setSelectedMinistry] = useState('');
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');

  // Form inputs
  const [groupName, setGroupName] = useState('');
  const [selectedPersona, setSelectedPersona] = useState('thriving');
  const [historicalWeeks, setHistoricalWeeks] = useState(8);
  const [includeChat, setIncludeChat] = useState(true);
  const [deleteOrphanedUsers, setDeleteOrphanedUsers] = useState(true);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingMinistries, setLoadingMinistries] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [error, setError] = useState(null);
  const [seedResult, setSeedResult] = useState(null);
  const [deleteResult, setDeleteResult] = useState(null);

  // Load organizations on mount
  useEffect(() => {
    const loadOrganizations = async () => {
      try {
        setLoadingOrgs(true);
        const response = await founderDashboardAPI.getOrganizationsList();
        setOrganizations(response.data.organizations || []);
      } catch (err) {
        console.error('[PersonaSeed] Failed to load organizations:', err);
        setError('Failed to load organizations. Confirm your Fold access and backend connection.');
      } finally {
        setLoadingOrgs(false);
      }
    };
    loadOrganizations();
  }, []);

  // Load ministries when org changes
  const loadMinistries = useCallback(async (orgId) => {
    if (!orgId) {
      setMinistries([]);
      setSelectedMinistry('');
      return;
    }

    try {
      setLoadingMinistries(true);
      const response = await seedAPI.listMinistriesForOrg(orgId);
      setMinistries(response.data.ministries || []);
      setSelectedMinistry('');
    } catch (err) {
      console.error('[PersonaSeed] Failed to load ministries:', err);
      setError('Failed to load ministries for organization.');
    } finally {
      setLoadingMinistries(false);
    }
  }, []);

  // Load groups when org changes
  const loadGroups = useCallback(async (orgId) => {
    if (!orgId) {
      setGroups([]);
      setSelectedGroup('');
      return;
    }

    try {
      setLoadingGroups(true);
      const response = await seedAPI.listGroupsForOrg(orgId);
      setGroups(response.data.groups || []);
      setSelectedGroup('');
    } catch (err) {
      console.error('[PersonaSeed] Failed to load groups:', err);
      setError('Failed to load groups for organization.');
    } finally {
      setLoadingGroups(false);
    }
  }, []);

  // Handle org selection
  const handleOrgChange = (orgId) => {
    setSelectedOrg(orgId);
    setError(null);
    setSeedResult(null);
    setDeleteResult(null);
    loadMinistries(orgId);
    loadGroups(orgId);
  };

  // Handle seeding a persona group
  const handleSeedGroup = async () => {
    if (!selectedOrg || !selectedMinistry || !groupName || !selectedPersona) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError(null);
    setSeedResult(null);

    try {
      const response = await seedAPI.seedPersonaGroup({
        organizationId: selectedOrg,
        ministryId: selectedMinistry,
        groupName,
        persona: selectedPersona,
        historicalWeeks,
        includeChat
      });

      setSeedResult(response.data);
      setGroupName(''); // Clear the name for next group
      loadGroups(selectedOrg); // Refresh groups list
    } catch (err) {
      console.error('[PersonaSeed] Error seeding group:', err);
      setError(err.response?.data?.message || err.message || 'Failed to seed persona group');
    } finally {
      setLoading(false);
    }
  };

  // Handle deleting a group
  const handleDeleteGroup = async () => {
    if (!selectedGroup) {
      setError('Please select a group to delete.');
      return;
    }

    // Find group name for confirmation
    const group = groups.find(g => g.id === selectedGroup);
    if (!window.confirm(`Are you sure you want to delete "${group?.name}"?\n\nThis will permanently delete:\n- All attendance records\n- All RSVPs\n- All events\n- All members\n- All prayers (Firestore)\n- All chat messages (Firestore)\n\nThis action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setDeleteResult(null);

    try {
      const response = await seedAPI.deleteGroupCompletely(selectedGroup, deleteOrphanedUsers);

      setDeleteResult(response.data);
      setSelectedGroup('');
      loadGroups(selectedOrg); // Refresh groups list
    } catch (err) {
      console.error('[PersonaSeed] Error deleting group:', err);
      setError(err.response?.data?.message || err.message || 'Failed to delete group');
    } finally {
      setLoading(false);
    }
  };

  // Get trend icon for persona
  const getTrendIcon = (trend) => {
    switch (trend) {
      case 'up':
        return <ArrowTrendingUpIcon className="w-4 h-4 text-green-600" />;
      case 'down':
        return <ArrowTrendingDownIcon className="w-4 h-4 text-red-600" />;
      default:
        return <MinusIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  // Get color classes for persona
  const getPersonaColors = (persona) => {
    const colors = {
      green: 'bg-green-50 border-green-200 text-green-800',
      blue: 'bg-blue-50 border-blue-200 text-blue-800',
      yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      orange: 'bg-orange-50 border-orange-200 text-orange-800',
      red: 'bg-red-50 border-red-200 text-red-800',
      purple: 'bg-purple-50 border-purple-200 text-purple-800'
    };
    return colors[PERSONA_DEFINITIONS[persona]?.color] || colors.blue;
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-deep-navy mb-2 flex items-center gap-2">
          <UserGroupIcon className="w-8 h-8" />
          Persona Group Seeding
        </h1>
        <p className="text-gray-600">
          Seed groups with predictable engagement patterns for testing Group Health features.
        </p>
      </div>

      {/* Info Banner */}
      <div className="card bg-blue-50 border-2 border-storm-blue/30 mb-6">
        <h3 className="font-semibold text-storm-blue mb-2">How This Works</h3>
        <ul className="text-sm text-gray-700 space-y-1">
          <li>• Each persona creates a group with <strong>10 members</strong> having specific engagement patterns</li>
          <li>• Generates <strong>8 weeks</strong> of historical attendance, events, prayers, and chat messages</li>
          <li>• Members have varied health statuses that result in predictable Group Health scores</li>
          <li>• Perfect for testing the Group Health deep-dive dashboard and member cards</li>
        </ul>
      </div>

      {/* Organization & Ministry Selection */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
          Step 1: Select Organization & Ministry
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="organization" className="label">
              Organization *
            </label>
            {loadingOrgs ? (
              <div className="input bg-gray-100">Loading organizations...</div>
            ) : (
              <select
                id="organization"
                value={selectedOrg}
                onChange={(e) => handleOrgChange(e.target.value)}
                className="input"
              >
                <option value="">Select an organization...</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div>
            <label htmlFor="ministry" className="label">
              Ministry *
            </label>
            {loadingMinistries ? (
              <div className="input bg-gray-100">Loading ministries...</div>
            ) : (
              <select
                id="ministry"
                value={selectedMinistry}
                onChange={(e) => setSelectedMinistry(e.target.value)}
                className="input"
                disabled={!selectedOrg}
              >
                <option value="">Select a ministry...</option>
                {ministries.map((ministry) => (
                  <option key={ministry.id} value={ministry.id}>
                    {ministry.name} ({ministry.campus}) - {ministry.groupCount} groups
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>

      {/* Persona Selection Grid */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <SparklesIcon className="w-5 h-5 text-yellow-500" />
          Step 2: Choose a Persona
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(PERSONA_DEFINITIONS).map(([key, persona]) => {
            const PersonaIcon = persona.icon;
            const isSelected = selectedPersona === key;

            return (
              <button
                key={key}
                onClick={() => setSelectedPersona(key)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  isSelected
                    ? `${getPersonaColors(key)} border-2 ring-2 ring-offset-2 ring-storm-blue`
                    : 'bg-white border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <PersonaIcon className={`w-5 h-5 ${isSelected ? '' : 'text-gray-500'}`} />
                    <span className="font-semibold">{persona.name}</span>
                  </div>
                  {getTrendIcon(persona.trend)}
                </div>
                <div className="text-2xl font-bold mb-1">{persona.score}</div>
                <p className="text-xs text-gray-600">{persona.description}</p>
                <div className="mt-2 flex gap-2 text-xs">
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded">
                    {persona.memberMix.healthy} healthy
                  </span>
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 rounded">
                    {persona.memberMix.needsAttention} attention
                  </span>
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded">
                    {persona.memberMix.atRisk} at-risk
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Seed Options */}
      <div className="card mb-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <PlusIcon className="w-5 h-5 text-green-600" />
          Step 3: Seed New Group
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="groupName" className="label">
              Group Name *
            </label>
            <input
              id="groupName"
              type="text"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              className="input"
              placeholder={`${PERSONA_DEFINITIONS[selectedPersona]?.name} Test Group`}
            />
          </div>

          <div>
            <label htmlFor="historicalWeeks" className="label">
              Historical Weeks
            </label>
            <select
              id="historicalWeeks"
              value={historicalWeeks}
              onChange={(e) => setHistoricalWeeks(parseInt(e.target.value))}
              className="input"
            >
              <option value={4}>4 weeks</option>
              <option value={8}>8 weeks (recommended)</option>
              <option value={12}>12 weeks</option>
              <option value={16}>16 weeks</option>
            </select>
          </div>
        </div>

        <div className="flex items-center gap-6 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={includeChat}
              onChange={(e) => setIncludeChat(e.target.checked)}
              className="w-4 h-4 text-storm-blue rounded"
            />
            <span className="text-sm">Include chat messages</span>
          </label>
        </div>

        <button
          onClick={handleSeedGroup}
          disabled={loading || !selectedOrg || !selectedMinistry || !groupName}
          className={`btn-primary flex items-center justify-center gap-2 ${
            loading || !selectedOrg || !selectedMinistry || !groupName
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-green-600 hover:bg-green-700'
          }`}
        >
          {loading ? (
            <>
              <span className="animate-spin">&#8987;</span>
              Seeding Group...
            </>
          ) : (
            <>
              <PlusIcon className="w-5 h-5" />
              Seed {PERSONA_DEFINITIONS[selectedPersona]?.name} Group
            </>
          )}
        </button>
      </div>

      {/* Delete Group Section */}
      <div className="card mb-6 bg-red-50 border-red-200">
        <h2 className="text-xl font-semibold text-red-900 mb-4 flex items-center gap-2">
          <TrashIcon className="w-5 h-5 text-red-600" />
          Delete Group (Cleanup)
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label htmlFor="deleteGroup" className="label text-red-800">
              Select Group to Delete
            </label>
            {loadingGroups ? (
              <div className="input bg-red-100">Loading groups...</div>
            ) : (
              <select
                id="deleteGroup"
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                className="input border-red-300"
                disabled={!selectedOrg}
              >
                <option value="">Select a group...</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name} ({group.memberCount} members) - {group.ministryName}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex items-end">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={deleteOrphanedUsers}
                onChange={(e) => setDeleteOrphanedUsers(e.target.checked)}
                className="w-4 h-4 text-red-600 rounded"
              />
              <span className="text-sm text-red-800">Delete orphaned users (recommended)</span>
            </label>
          </div>
        </div>

        <button
          onClick={handleDeleteGroup}
          disabled={loading || !selectedGroup}
          className={`btn-primary flex items-center justify-center gap-2 ${
            loading || !selectedGroup
              ? 'bg-gray-400 cursor-not-allowed'
              : 'bg-red-600 hover:bg-red-700'
          }`}
        >
          {loading ? (
            <>
              <span className="animate-spin">&#8987;</span>
              Deleting Group...
            </>
          ) : (
            <>
              <TrashIcon className="w-5 h-5" />
              Delete Group & All Data
            </>
          )}
        </button>
      </div>

      {/* Error Messages */}
      {error && (
        <div className="mt-6 bg-danger-50 border border-danger-200 text-danger-800 px-4 py-3 rounded-lg">
          <p className="font-semibold flex items-center gap-2">
            <ExclamationTriangleIcon className="w-5 h-5" />
            Error
          </p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Seed Success */}
      {seedResult && (
        <div className="mt-6 bg-success-50 border border-success-200 text-success-800 px-4 py-3 rounded-lg">
          <p className="font-semibold flex items-center gap-2">
            <CheckCircleIcon className="w-5 h-5" />
            Group Seeded Successfully!
          </p>
          <p className="text-sm">
            Created group "{seedResult.group?.name}" with expected health score: {seedResult.expectedHealthScore}
          </p>
        </div>
      )}

      {/* Delete Success */}
      {deleteResult && (
        <div className="mt-6 bg-yellow-50 border border-yellow-200 text-yellow-800 px-4 py-3 rounded-lg">
          <p className="font-semibold flex items-center gap-2">
            <TrashIcon className="w-5 h-5" />
            Group Deleted Successfully!
          </p>
          <p className="text-sm">
            Deleted "{deleteResult.deleted?.group?.name}" and all associated data.
          </p>
        </div>
      )}

      {/* API Response */}
      {seedResult && <JsonViewer data={seedResult} title="Seed Result" />}
      {deleteResult && <JsonViewer data={deleteResult} title="Delete Result" />}

      {/* Usage Tips */}
      <div className="card bg-gray-50 mt-6">
        <h3 className="font-semibold text-storm-blue mb-3">Tips for Testing Group Health</h3>
        <ul className="text-sm text-gray-700 space-y-2">
          <li>
            <strong>For comprehensive testing:</strong> Create one of each persona type to see the full range of health scores
          </li>
          <li>
            <strong>Thriving/Growing:</strong> Test the positive states and celebration UI elements
          </li>
          <li>
            <strong>Struggling/Crisis:</strong> Test warning states, at-risk member cards, and intervention prompts
          </li>
          <li>
            <strong>Recovering:</strong> Test the trend indicators and improvement tracking
          </li>
          <li>
            <strong>Chat messages:</strong> Enable chat to test the engagement score component
          </li>
        </ul>
      </div>
    </div>
  );
}
